import { z } from 'zod';
import { marginPctSchema } from './rates';

/** Settings (spec §3): one place, admin-editable, stored in the DB, seeded with defaults. */

export const companySettingsSchema = z.object({
  name: z.string().default('data-claw.de'),
  tagline: z.object({ en: z.string(), de: z.string() }),
  website: z.string().default('www.data-claw.de'),
  email: z.string().default('catalin.craciun@data-claw.de'),
  /** Shared mailbox printed on documents by users who hide their name (Account › Documents). */
  genericEmail: z.string().default('hello@data-claw.de'),
  contact: z.object({
    name: z.string(),
    role: z.object({ en: z.string(), de: z.string() }),
    email: z.string(),
    phone: z.string().default(''),
  }),
  colors: z.object({
    black: z.string(),
    violet: z.string(),
    violetTint: z.string(),
    white: z.string(),
    textGrey: z.string(),
    lineGrey: z.string(),
    taglineGrey: z.string(),
  }),
  font: z.string().default('Satoshi'),
  /** File IDs (File rows) or "bundled" for the logos shipped with the app. */
  logos: z
    .object({
      logoHorizontalLight: z.string().default('bundled'),
      symbolViolet: z.string().default('bundled'),
    })
    .default({ logoHorizontalLight: 'bundled', symbolViolet: 'bundled' }),
});
export type CompanySettings = z.infer<typeof companySettingsSchema>;

export const driveSettingsSchema = z.object({
  rootFolderId: z.string().default(''),
  openJdsFolderId: z.string().default(''),
  opportunitiesFolderId: z.string().default(''),
  candidatesFolderId: z.string().default(''),
  templatesFolderId: z.string().default(''),
});
export type DriveSettings = z.infer<typeof driveSettingsSchema>;

export const settingsSchema = z.object({
  margin_pct: marginPctSchema.default(20),
  hours_per_day: z.number().positive().default(8),
  feedback_due_working_days: z.number().int().min(0).default(3),
  urgent_start_days: z.number().int().min(0).default(7),
  consent_retention_months: z.number().int().min(1).default(24),
  forbidden_terms: z.array(z.string()).default(['TechProjectsNow']),
  company: companySettingsSchema,
  drive: driveSettingsSchema.default({}),
  date_format: z.string().default('DD.MM.YYYY'),
  time_zone: z.string().default('Europe/Bucharest'),
  currency: z.literal('EUR').default('EUR'),
});
export type Settings = z.infer<typeof settingsSchema>;
export type SettingKey = keyof Settings;

export const SETTING_KEYS = Object.keys(settingsSchema.shape) as SettingKey[];

export const DEFAULT_COMPANY: CompanySettings = {
  name: 'data-claw.de',
  tagline: {
    en: 'Senior data, AI and DevOps teams for German enterprises. Nearshore from Romania.',
    de: 'Senior-Teams für Data, AI und DevOps für deutsche Unternehmen. Nearshore aus Rumänien.',
  },
  website: 'www.data-claw.de',
  email: 'catalin.craciun@data-claw.de',
  genericEmail: 'hello@data-claw.de',
  contact: {
    name: 'Catalin',
    role: { en: 'Co-Founder, data-claw.de', de: 'Co-Founder, data-claw.de' },
    email: 'catalin.craciun@data-claw.de',
    phone: '',
  },
  colors: {
    black: '0A0B16',
    violet: '7758F9',
    violetTint: 'F7F8F8',
    white: 'F9F9F9',
    textGrey: '777777',
    lineGrey: 'DCE0E4',
    taglineGrey: 'AFAFAF',
  },
  font: 'Satoshi',
  logos: { logoHorizontalLight: 'bundled', symbolViolet: 'bundled' },
};

export const DEFAULT_SETTINGS: Settings = settingsSchema.parse({
  company: DEFAULT_COMPANY,
});

/** Admin-editable option lists (spec §4): key → seed values. */
export const OPTION_LISTS: Record<string, string[]> = {
  department: ['Engineering', 'Sales', 'Marketing', 'Operations', 'Design'],
  language: ['English', 'German', 'Romanian', 'French', 'Spanish', 'Italian', 'Hungarian'],
  skill: [
    'SAP S/4HANA',
    'SAP Integration Suite (CPI)',
    'SAP BTP',
    'SAP CAP',
    'SAPUI5 / Fiori',
    'ABAP',
    'SAP HANA',
    'SAP Datasphere',
    'SuccessFactors',
    'Business Analysis',
    'Requirements Engineering',
    'SQL',
    'Data Engineering',
    'AI / ML',
    'Project Management',
    'Agile / Scrum',
    'Android / Mobile',
  ],
  country: ['Romania', 'Poland', 'Germany', 'Other'],
};

/** Terms that must never appear in generated documents: settings + every intermediary name (+ client name per position). */
export function effectiveForbiddenTerms(
  settingsTerms: readonly string[],
  intermediaryNames: readonly string[],
  extra: readonly (string | null | undefined)[] = [],
): string[] {
  const all = [
    ...settingsTerms,
    ...intermediaryNames,
    ...extra.filter((x): x is string => !!x && x.trim().length > 1),
  ];
  return Array.from(new Set(all.map((t) => t.trim()).filter((t) => t.length > 1)));
}
